import { createContext, useContext, ReactNode, useCallback, useEffect, useState } from 'react';
import {
  HomeScreenPreference,
  HomeScreenType,
  getHomeScreenPreference,
  setHomeScreenPreference,
  getHomeScreenRoute,
} from '../utils/homeScreenPreference';

interface HomeScreenContextType {
  preference: HomeScreenPreference;
  setHomeScreen: (screen: HomeScreenType) => void;
  getHomeRoute: () => string;
}

const HomeScreenContext = createContext<HomeScreenContextType | undefined>(undefined);

interface HomeScreenProviderProps {
  children: ReactNode;
}

export function HomeScreenProvider({ children }: HomeScreenProviderProps) {
  const [preference, setPreference] = useState<HomeScreenPreference>(getHomeScreenPreference());

  const setHomeScreen = useCallback((screen: HomeScreenType) => {
    const updated = setHomeScreenPreference(screen);
    setPreference(updated);
  }, []);

  const getHomeRoute = useCallback(() => {
    return getHomeScreenRoute(preference);
  }, [preference]);

  // Listen for storage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = () => {
      setPreference(getHomeScreenPreference());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <HomeScreenContext.Provider
      value={{
        preference,
        setHomeScreen,
        getHomeRoute,
      }}
    >
      {children}
    </HomeScreenContext.Provider>
  );
}

export function useHomeScreen(): HomeScreenContextType {
  const context = useContext(HomeScreenContext);
  if (context === undefined) {
    throw new Error('useHomeScreen must be used within a HomeScreenProvider');
  }
  return context;
}
